import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, PhoneIcon, MailIcon, MessageSquareIcon, PackageIcon, TargetIcon, ShieldCheckIcon } from 'lucide-react';
import { MerchantCustomer, customerTags, initials } from '../../data/customers';
import { Order, money, STATUS_LABEL, PAYMENT_LABEL } from '../../data/orders';
type Tab = 'Overview' | 'Orders' | 'Leads' | 'Conversations';

/**
 * One customer, as this merchant sees them. Deliberately not a CRM: it answers
 * who they are, how they arrived, and what they've done WITH THIS MERCHANT —
 * nothing from any other merchant, ever.
 */
export function CustomerProfile({
  customer,
  orders,
  onClose,
  onOpenOrder
}: {
  customer: MerchantCustomer;
  /** Already scoped to this merchant and this customer by the caller. */
  orders: Order[];
  onClose: () => void;
  onOpenOrder?: (order: Order) => void;
}) {
  const [tab, setTab] = useState<Tab>('Overview');
  const tags = customerTags(customer);
  return <div className="fixed inset-0 z-[70] flex justify-end" data-id="element-2007" data-mp="1u4i4xm9hzbu0">
      <div className="absolute inset-0 bg-dark-navy/30 backdrop-blur-sm" onClick={onClose} data-id="element-2008" data-mp="1u74axhgqpja4" />
      <motion.aside initial={{
      x: 32,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full max-w-xl bg-white h-full flex flex-col shadow-2xl" data-id="element-2009" data-mp="235pr55yqom7q">
        <header className="flex-shrink-0 px-7 pt-7 pb-5 border-b border-slate-100" data-id="element-2010" data-mp="16gqihpu8tif0">
          <div className="flex items-start gap-4" data-id="element-2011" data-mp="1k73uq4e63ja5">
            <span className="w-12 h-12 rounded-2xl bg-primary-indigo/[0.07] flex items-center justify-center text-sm font-bold text-primary-indigo flex-shrink-0" data-id="element-2012" data-mp="1k8omxnd0eq4u">
              {initials(customer.name)}
            </span>
            <div className="min-w-0 flex-1" data-id="element-2013" data-mp="mrpty2ra3ao6">
              <h2 className="font-display font-bold text-xl text-dark-navy truncate" data-id="element-2014" data-mp="mqwwxti4fzj8">
                {customer.name}
              </h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[13px] text-slate-500" data-id="element-2015" data-mp="2rsslpv0j9qd3">
                <span data-id="element-2016" data-mp="2wjr3z0530yiz">{customer.phone}</span>
                {customer.email && <span data-id="element-2017" data-mp="2wkal4emwrivy">{customer.email}</span>}
              </div>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-2 -mt-1 -mr-2 rounded-lg text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-2018" data-mp="31hak65lcbldk">
              <XIcon className="w-4 h-4" data-id="element-2019" data-mp="2dt113bfihbq4" />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-3 mt-6" data-id="element-2020" data-mp="2dv418oadol8k">
            <Stat label="Orders" value={String(customer.orders)} data-id="element-2021" data-mp="2hvnv9iznn8qh" />
            <Stat label="Total spent" value={customer.spend ? money(customer.spend) : '—'} data-id="element-2022" data-mp="3p8g7t6xivhyf" />
            <Stat label="First seen" value={customer.firstSeen} data-id="element-2023" data-mp="3t8qfqp4vkifx" />
            <Stat label="Last activity" value={customer.lastActivity} data-id="element-2024" data-mp="1hm6f0u22cc25" />
          </div>

          <div className="flex gap-1 mt-6 -mb-5" data-id="element-2025" data-mp="1dkt62b3obsr0">
            {(['Overview', 'Orders', 'Leads', 'Conversations'] as Tab[]).map(t => <button key={t} onClick={() => setTab(t)} className={`relative px-3.5 py-2.5 text-[13px] font-semibold transition-colors duration-150 ease-out ${tab === t ? 'text-primary-indigo' : 'text-slate-500 hover:text-dark-navy'}`} data-id="element-2026" data-mp="18s9tnhgq33ns">
                {t}
                {tab === t && <motion.span layoutId="customerTab" className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-indigo rounded-full" data-id="element-2027" data-mp="puqia76sl8y7" />}
              </button>)}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6" data-id="element-2028" data-mp="2fa6n5adoo8ia">
          {tab === 'Overview' && <>
              <Block title="How they reached you" data-id="element-2029" data-mp="1bz0yzjbqz1bc">
                <Row label="Source" value={`${customer.source}`} data-id="element-2030" data-mp="1bx6bbx93cv3k" />
                <Row label="First seen" value={customer.firstSeen} data-id="element-2031" data-mp="1gx2k09sqhapg" />
                <Row label="Last activity" value={customer.lastActivity} data-id="element-2032" data-mp="12z4778u29mc2" />
              </Block>

              {tags.length > 0 && <div data-id="element-2033" data-mp="17w4c3gf6cj1l">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-2034" data-mp="17zs5mfkmd5gb">
                    Tags
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3" data-id="element-2035" data-mp="y018e64occcj">
                    {tags.map(t => <span key={t} className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100 text-[11px] font-semibold text-slate-600" data-id="element-2036" data-mp="k8w7l0pkle0m">
                        {t}
                      </span>)}
                  </div>
                </div>}

              <Block title="Contact" data-id="element-2037" data-mp="faunqdkblao3">
                <div className="flex flex-wrap gap-2" data-id="element-2038" data-mp="3rrt3283ma8u7">
                  <Action icon={MessageSquareIcon} label="Message" data-id="element-2039" data-mp="3rufjkx8hjyok" />
                  <Action icon={PhoneIcon} label="Call" data-id="element-2040" data-mp="3mv2lfgc5z56o" />
                  <Action icon={MailIcon} label="Email" data-id="element-2041" data-mp="5lxhpa6d2ro0" />
                </div>
              </Block>

              <p className="flex items-start gap-2 text-[13px] text-slate-500 leading-relaxed" data-id="element-2042" data-mp="lrzeklzehil">
                <ShieldCheckIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-2043" data-mp="elkko0eqo5j6" />
                You only ever see this person's activity with your business.
                Their activity with other Talab shops is never shared.
              </p>
            </>}

          {tab === 'Orders' && (orders.length === 0 ? <Empty icon={PackageIcon} title="No orders yet" desc="This is completely normal — plenty of customers browse and ask questions before they order." data-id="element-2044" data-mp="jdkpa65r7mm3" /> : <div className="space-y-3" data-id="element-2045" data-mp="10eoxhmuf87p6">
                {orders.map(o => <button key={o.id} onClick={() => onOpenOrder?.(o)} className="w-full text-left p-4 bg-white border border-slate-200 rounded-2xl hover:border-primary-indigo/40 transition-colors duration-150 ease-out" data-id="element-2046" data-mp="15d9msr84x6yu">
                    <div className="flex items-center justify-between gap-3" data-id="element-2047" data-mp="1j4ogpt3kysko">
                      <p className="text-sm font-bold text-dark-navy" data-id="element-2048" data-mp="1j6r4e6h9g9m8">
                        {o.lines[0].name}
                      </p>
                      <p className="text-sm font-bold text-dark-navy flex-shrink-0" data-id="element-2049" data-mp="1t2b1gw7k9eck">
                        {money(o.total)}
                      </p>
                    </div>
                    <p className="text-[13px] text-slate-500 mt-1" data-id="element-2050" data-mp="3uu7anz69ac46">
                      {o.id} · {STATUS_LABEL[o.status]} ·{' '}
                      {PAYMENT_LABEL[o.payment]} · {o.when}
                    </p>
                  </button>)}
              </div>)}

          {tab === 'Leads' && (customer.leads === 0 ? <Empty icon={TargetIcon} title="No leads" desc="Leads appear when this customer expresses interest your team can follow up on." data-id="element-2051" data-mp="2c5weoskv8iqy" /> : <div className="space-y-3" data-id="element-2052" data-mp="234y5lvvkll1i">
                {Array.from({
            length: customer.leads
          }).map((_, i) => <div key={i} className="p-4 bg-white border border-slate-200 rounded-2xl" data-id="element-2053" data-mp="2vwhwm1yes3e2" data-mpi={i}>
                    <p className="text-sm font-bold text-dark-navy" data-id="element-2054" data-mp="2qy6i3dx654pi">
                      {LEAD_TITLES[i % LEAD_TITLES.length]}
                    </p>
                    <p className="text-[13px] text-slate-500 mt-1" data-id="element-2055" data-mp="1fhztfaehgz9e">
                      From {customer.source} · {customer.lastActivity}
                    </p>
                  </div>)}
              </div>)}

          {tab === 'Conversations' && (customer.conversations === 0 ? <Empty icon={MessageSquareIcon} title="No conversations" desc="Messages between your assistant, your team and this customer will appear here." data-id="element-2056" data-mp="3fkr9snhsowdz" /> : <div className="space-y-3" data-id="element-2057" data-mp="3tb4i4qylmd3u">
                {Array.from({
            length: customer.conversations
          }).map((_, i) => <div key={i} className="p-4 bg-white border border-slate-200 rounded-2xl" data-id="element-2058" data-mp="23s0katszxne" data-mpi={i}>
                    <p className="text-sm font-bold text-dark-navy" data-id="element-2059" data-mp="3ojco26g8hp8y">
                      {CONVO_TITLES[i % CONVO_TITLES.length]}
                    </p>
                    <p className="text-[13px] text-slate-500 mt-1 line-clamp-1" data-id="element-2060" data-mp="g2863yfxji3y">
                      {CONVO_SNIPPETS[i % CONVO_SNIPPETS.length]}
                    </p>
                    <button className="mt-3 text-[13px] font-semibold text-primary-indigo hover:underline" data-id="element-2061" data-mp="25jvjiwumlhi9">
                      Open conversation
                    </button>
                  </div>)}
              </div>)}
        </div>
      </motion.aside>
    </div>;
}
const LEAD_TITLES = ['Asked about monthly rates for a 7-seater', 'Requested airport pickup and delivery', 'Comparing Prado and Land Cruiser'];
const CONVO_TITLES = ['Shop assistant', 'Website chat', 'Corporate enquiry'];
const CONVO_SNIPPETS = ['Do you deliver to Abu Dhabi on weekends?', 'What is included in the insurance cover?', 'We need four vehicles for six months.'];
function Stat({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-2062" data-mp="371nuku74bhjw">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-2063" data-mp="3714elj11338i">
        {label}
      </p>
      <p className="text-sm font-bold text-dark-navy mt-1 truncate" data-id="element-2064" data-mp="330ar2qrhzqg5">{value}</p>
    </div>;
}
function Block({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-2065" data-mp="3ez21ch6v9wp0">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-2066" data-mp="3ezbs46zqk7vr">
        {title}
      </p>
      <div className="mt-3 space-y-2.5" data-id="element-2067" data-mp="3a5pdwadxbvxu">{children}</div>
    </div>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between gap-4" data-id="element-2068" data-mp="309w2wvfusai6">
      <span className="text-[13px] text-slate-500" data-id="element-2069" data-mp="3079ocbujouqg">{label}</span>
      <span className="text-[13px] font-semibold text-dark-navy" data-id="element-2070" data-mp="2v8zqtulcli1o">{value}</span>
    </div>;
}
function Action({
  icon: Icon,
  label
}: {
  icon: typeof PhoneIcon;
  label: string;
}) {
  return <button className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-[13px] font-semibold text-slate-600 hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-2071" data-mp="2mbfm2boa13mb">
      <Icon className="w-4 h-4" data-id="element-2072" data-mp="s2af3a9w05x2" />
      {label}
    </button>;
}
function Empty({
  icon: Icon,
  title,
  desc
}: {
  icon: typeof PackageIcon;
  title: string;
  desc: string;
}) {
  return <div className="py-10 text-center" data-id="element-2073" data-mp="11s2echsxln7v">
      <span className="w-11 h-11 rounded-2xl bg-slate-50 border border-slate-100 inline-flex items-center justify-center" data-id="element-2074" data-mp="11t54uomcdnn9">
        <Icon className="w-5 h-5 text-slate-400" data-id="element-2075" data-mp="16q3uouyz38fn" />
      </span>
      <p className="text-sm font-bold text-dark-navy mt-4" data-id="element-2076" data-mp="946krlynsbfb">{title}</p>
      <p className="text-[13px] text-slate-500 mt-1.5 max-w-sm mx-auto leading-relaxed" data-id="element-2077" data-mp="e1zo878kurbw">
        {desc}
      </p>
    </div>;
}